import type { AsyncDataOptions } from '#app'
import { storeToRefs } from 'pinia'

import { removeEmojis } from '@/utils/common'
import { AsyncDataKeys, getAsyncDataKey } from '@/constants/async-data-keys'

import { getProductComments, getProductReviewTags, getProductReviewTagsCount } from '@/service/product'
import { putReviewLike, deleteReviewLike } from '@/service/review'

import type { GetProductCommentsParams } from '@/types/product'
import type { ReviewReviewResponse } from '@/types/api'
import { ResponseCode } from '@/types/service'

import { useAuthStore } from '@/store/auth'

export function useComment(name: ReadonlyRef<string | undefined>) {
  const removedEmojisName = computed(() => removeEmojis(name.value ?? ''))
  const isDefaultAvatarShown = computed(() => !removedEmojisName.value)
  const firstLetter = computed(() => removedEmojisName.value[0])

  const { localeUpperCaseString: commenterFirstLetter } = useUpperCase(firstLetter)

  return {
    isDefaultAvatarShown,
    commenterFirstLetter,
  }
}

interface UseGetProductCommentsOpts {
  debounce?: boolean
  useDual?: boolean
  asyncDataOpts?: AsyncDataOptions<ReviewReviewResponse>
}

export async function useGetProductComments(
  key: string,
  pid: ReadonlyRef<string | number | undefined>,
  queryParams: ComputedRef<GetProductCommentsParams>,
  { debounce = false, useDual = false, asyncDataOpts = {} }: UseGetProductCommentsOpts = {}
) {
  const getProductCommentsWithDebounce = useDebounceFn(getProductComments, 300)
  const ussAsyncDataFunc = useDual ? useDualAsyncData : useAsyncData

  const asyncData = await ussAsyncDataFunc(
    key,
    async () => {
      if (!pid.value) {
        throw new Error('pid is required')
      }

      const apiFunc = debounce ? getProductCommentsWithDebounce : getProductComments
      const { data } = await apiFunc(pid.value, queryParams.value)

      return data
    },
    { watch: [queryParams], ...asyncDataOpts }
  )

  const isProductCommentsLoading = computed(() => ['idle', 'pending'].includes(asyncData.status.value))

  return { ...asyncData, isProductCommentsLoading }
}

export async function useGetProductReviewTags(id: ReadonlyRef<string | undefined>) {
  const asyncData = await useLazyAsyncData(
    getAsyncDataKey(AsyncDataKeys.PRODUCT_REVIEW_TAGS, [id.value]),
    async () => {
      if (!id.value) {
        return null
      }

      const res = await getProductReviewTags(id.value)

      return res.data.rating
    },
    { watch: [id], server: false }
  )

  return asyncData
}

export async function useGetProductReviewTagsCount(pid: ReadonlyRef<string | number | undefined>) {
  const asyncData = await useLazyAsyncData(
    getAsyncDataKey(AsyncDataKeys.PRODUCT_REVIEW_TAGS_COUNT, [pid.value]),
    async () => {
      if (!pid.value) {
        return null
      }

      const res = await getProductReviewTagsCount(pid.value)

      return res.data
    },
    { watch: [pid], server: false }
  )

  return asyncData
}

type UseLikeReviewArgs = {
  isUseful: ReadonlyRef<boolean>
  usefulCount: ReadonlyRef<number>
}

export function useLikeReview({ isUseful, usefulCount }: UseLikeReviewArgs) {
  const authStore = useAuthStore()
  const { isLogin } = storeToRefs(authStore)

  const isLiked = ref(isUseful.value)
  const previousIsLiked = ref(isLiked.value)
  const likesCount = ref(usefulCount.value)

  const likedIconName = computed(() => (isLiked.value ? 'heart' : 'heart-line'))
  const likedIconColor = computed(() => (isLiked.value ? 'red-50' : 'gray-80'))

  let controller = new AbortController()

  watch(isUseful, newIsUseful => {
    isLiked.value = newIsUseful
  })

  watch(isLogin, newIsLogin => {
    if (!newIsLogin) {
      isLiked.value = false
    }
  })

  async function fetchLikeReviewApi(id?: number | string) {
    if (typeof id === 'undefined') {
      return
    }

    const apiFunc = isLiked.value ? deleteReviewLike : putReviewLike

    controller.abort()
    controller = new AbortController()

    isLiked.value = !isLiked.value

    try {
      await apiFunc(id, { signal: controller.signal })

      if (previousIsLiked.value !== isLiked.value) {
        likesCount.value += previousIsLiked.value ? -1 : 1
        previousIsLiked.value = isLiked.value
      }
    } catch (error: any) {
      if (error?.options?.signal.aborted || error?.data?.code === ResponseCode.DUPLICATE_KEY) {
        return
      }

      isLiked.value = !isLiked.value
    }
  }

  return { isLiked, likesCount, likedIconName, likedIconColor, fetchLikeReviewApi }
}
